<!-- Componet for printing multiple slides -->
<template>
	<div
		class="print-container relative h-full w-full overflow-y-scroll pt-14 pr-8 pl-6 print:mt-8 print:max-w-300 print:min-w-300 print:p-0!"
	>
		<!-- Error bag -->
		<template v-if="!!errors.length">
			Errors occurred while loading memorial guides:
			<ul>
				<li v-for="error in errors">{{ error }}</li>
			</ul>
		</template>
		<!-- Overlay for preparing and cancelling printing-->
		<div
			v-if="showOverlay && errors.length === 0"
			class="fixed inset-0 z-50 flex items-center justify-center bg-white/90"
		>
			<div class="text-center">
				<p class="text-2xl font-semibold text-slate-700">
					{{ t('paymentOptionsSlide.print.overlay.preparing') }}
				</p>
				<p class="mt-2 text-sm text-slate-500">
					{{ t('paymentOptionsSlide.print.overlay.pleaseWait') }}
				</p>
			</div>
		</div>
		<!-- Only show forms if plan finished loading-->
		<template v-if="ready && errors.length === 0">
			<!-- Memorial Guide -->
			<section
				data-area="print"
				:class="{ 'print:break-after-page': true }"
			>
				<DashboardMemorialGuidesPrint :plans="plans" />
			</section>
		</template>
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import DashboardMemorialGuidesPrint from '@/components/home/DashboardMemorialGuidesPrint.vue'
import { usePresentationLocale } from '@/i18n'
import { useLazyRenderWaiter } from '@/mixins/lazyRenderWaiter'
import { usePlanVersionsV2 } from '@/mixins/planVersionsV2'
import { useCrmStore } from '@/stores/crm'
import { useSessionStore } from '@/stores/session'
import { onMounted, onBeforeUnmount, nextTick, ref } from 'vue'
import { useRoute } from 'vue-router'

const { t } = usePresentationLocale()

const route = useRoute()
const showOverlay = ref(true)
const ready = ref(false)
const plans = ref<Plan[]>([])
const errors = ref<string[]>([])

useLazyRenderWaiter(onPrintReady)
const crmStore = useCrmStore()
const sessionStore = useSessionStore()
const { getLatestPlanVersionsFromAppointment, loadPlansFromPlanVersions } =
	usePlanVersionsV2()

// After printing navigate back to the original page
async function handleAfterPrint() {
	showOverlay.value = false
}

// Print the current page after the content has rendered
onMounted(async () => {
	// Load plans
	const { appointmentId, sessionId } = route.params

	// at least it has one of the IDs
	if (!appointmentId && !sessionId) {
		errors.value.push('print page must have an appointment or session ID')
		return
	}

	const session = sessionStore.getSessionById(sessionId as string)
	const appointment = crmStore.findAppointment(appointmentId as string)

	try {
		const planVersions = Object.values(
			await getLatestPlanVersionsFromAppointment(session, appointment)
		)
		plans.value = await loadPlansFromPlanVersions(planVersions)
	} catch (error: any) {
		errors.value.push(error.message)
	}

	ready.value = true

	window.addEventListener('afterprint', handleAfterPrint)
})

async function onPrintReady() {
	showOverlay.value = false

	// wait for the overlay to hide before printing
	await nextTick()

	window.print()
}

// Clean up the event listeners
onBeforeUnmount(() => {
	window.removeEventListener('afterprint', handleAfterPrint)
})
</script>

<style scoped>
@import '@/assets/styles/memorialGuidePrint.css';
</style>
